import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, CircularProgress } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import HardwareIcon from '@mui/icons-material/Hardware';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const Dashboard = ({ selectedHardwareCount, systemReadyHardwareCount, videoCallHardwareCount, paymentHardwareCount, dispatchedHardwareCount }) => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState({
    totalSystems: 0,
    pendingPayments: 0,

    newEntriesToday: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNavigating, setIsNavigating] = useState(null); // State to track which card is navigating
  const [showSuccess, setShowSuccess] = useState(null); // State to show success icon

  useEffect(() => {
    const fetchSummaryData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/summary`);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }
        const data = await response.json();
        setSummaryData(data);
      } catch (error) {
        console.error('Error fetching summary data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSummaryData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Loading Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" color="error" gutterBottom>
          Error Loading Dashboard
        </Typography>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  const navItems = [
    { name: 'Hardware List', icon: <HardwareIcon />, path: '/hardware', count: selectedHardwareCount },
    { name: 'Hardware Selected', icon: <BuildCircleIcon />, path: '/hardware-selected', count: selectedHardwareCount },
    { name: 'System Ready', icon: <CheckCircleOutlineIcon />, path: '/system-ready', count: systemReadyHardwareCount },
    { name: 'Video Call', icon: <VideocamIcon />, path: '/video-call', count: videoCallHardwareCount },
    { name: 'Payment', icon: <PaymentIcon />, path: '/payment', count: paymentHardwareCount },
    { name: 'Dispatch', icon: <LocalShippingIcon />, path: '/dispatch', count: dispatchedHardwareCount },
    { name: 'Excel Data', icon: <InsertDriveFileIcon />,  path: '/excel-data'},
  ];

  const handleCardClick = (filterType, filterValue, itemPath, itemName) => {
    setIsNavigating(itemName);
    setTimeout(() => {
      setIsNavigating(null);
      setShowSuccess(itemName);
      setTimeout(() => {
        setShowSuccess(null);
        navigate(itemPath, { state: { filter: filterType, value: filterValue } });
      }, 500); // Duration for success icon
    }, 500); // Simulate navigation delay
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4} md={4}>
          <Card
            sx={{ height: '100%', boxShadow: 3, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' }, cursor: 'pointer' }}
            onClick={() => handleCardClick('all', null, '/customer-details', 'Total Systems')}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3, alignItems: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Systems
              </Typography>
              <Typography variant="h3" component="div">
                {summaryData.totalSystems}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <Card
            sx={{ height: '100%', boxShadow: 3, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' }, cursor: 'pointer' }}
            onClick={() => handleCardClick('paymentStatus', 'Pending', '/customer-details', 'Pending Payments')}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3, alignItems: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Pending Payments
              </Typography>
              <Typography variant="h3" component="div">
                {summaryData.pendingPayments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4} md={4}>
          <Card
            sx={{ height: '100%', boxShadow: 3, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' }, cursor: 'pointer' }}
            onClick={() => handleCardClick('date', new Date().toISOString().slice(0, 10), '/customer-details', 'New Entries Today')}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3, alignItems: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                New Entries Today
              </Typography>
              <Typography variant="h3" component="div">
                {summaryData.newEntriesToday}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Quick Navigation
      </Typography>
      <Grid container spacing={3}>
        {navItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.name}>
            <Card
              sx={{
                height: '100%',
                boxShadow: 3,
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': { transform: 'translateY(-5px) scale(1.03)', boxShadow: 6 },
                cursor: 'pointer',
                border: showSuccess === item.name ? '1px solid green' : '1px solid #e0e0e0',
              }}
              onClick={() => handleCardClick(item.filterType, item.filterValue, item.path, item.name)}
            >
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3, alignItems: 'center' }}>
                {isNavigating === item.name ? (
                  <CircularProgress size={24} color="inherit" />
                ) : showSuccess === item.name ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  item.icon
                )}
                <Typography variant="h6" component="div" sx={{ mt: 1, textAlign: 'center' }}>
                  {item.name}
                  <Typography variant="h5" component="span" sx={{ fontWeight: 'bold', display: 'block' }}>
                    ({item.count})
                  </Typography>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};



export default Dashboard;
